

import React, { useState, useEffect } from 'react';
import { Save, Loader2, User, FileBadge, CalendarClock, CheckCircle, XCircle, Lock, GraduationCap, AlertTriangle, FileText, Info } from 'lucide-react';
import { DATA_PANGKAT } from '../constants/data'; 
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const ProfilePage = ({ onSwitchTab }) => {
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pakStatus, setPakStatus] = useState({ isLocked: false, status: '' });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const BASE_URL = process.env.REACT_APP_API_URL;

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [missingFieldsList, setMissingFieldsList] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const filteredPangkat = DATA_PANGKAT.filter(item => item.gol.startsWith('III') || item.gol.startsWith('IV'));

  const [formData, setFormData] = useState({
    jenis_pak: 'Integrasi', full_name: '', nip: '', karpeg: '',
    tempat_lahir: '', tanggal_lahir: '', jenis_kelamin: '',
    jenis_jabatan: 'Guru', 
    golongan: '', pangkat: '', tmt_golongan: '', jabatan: '', tmt_jabatan: '', unit_kerja: '', 
    masa_kerja_tahun: '', masa_kerja_bulan: '', pendidikan_pangkat: '',
    is_pencantuman_gelar: 'Tidak', pendidikan_gelar_baru: '', nama_pendidikan_baru: '', tahun_gelar_baru: '', tahun_usulan_gelar: '',
    is_jabatan_sesuai: 'Ya', jabatan_baru: '', tmt_jabatan_baru: '',
    is_baru_naik_jenjang: 'Tidak', // STATE BARU UNTUK CHECKBOX 0 AK
    masa_penilaian_mulai: '', masa_penilaian_selesai: '2022-12-31' 
  });

  const getAutoJabatan = (gol) => {
    if (['III/a', 'III/b'].includes(gol)) return 'Guru Ahli Pertama';
    if (['III/c', 'III/d'].includes(gol)) return 'Guru Ahli Muda';
    if (['IV/a', 'IV/b', 'IV/c'].includes(gol)) return 'Guru Ahli Madya';
    if (['IV/d', 'IV/e'].includes(gol)) return 'Guru Ahli Utama';
    return '';
  };

  const showNotification = (msg, type = 'success') => {
    setToast({ show: true, message: msg, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const formatReviewDate = (dateStr) => {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const [resProfile, resStatus] = await Promise.all([
        fetch(`${BASE_URL}/api/profile`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${BASE_URL}/api/pak/status`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (resStatus.ok) {
        const statusData = await resStatus.json();
        if (statusData.submitted && statusData.status !== 'Ditolak') setPakStatus({ isLocked: true, status: statusData.status });
      }

      if (resProfile.ok) {
        const data = await resProfile.json();
        const formatDate = (ds) => ds ? ds.split('T')[0] : '';
        if (data && Object.keys(data).length > 0) {
          let defaultSelesai = formatDate(data.masa_penilaian_selesai) || '2025-12-31';
          if ((data.jenis_pak || 'Integrasi') === 'Integrasi') {
              defaultSelesai = '2022-12-31';
          }
          
          let isGuru = true;
          if (data.jabatan && !data.jabatan.toLowerCase().includes('guru')) {
              isGuru = false;
          }

          setFormData({
            ...formData, ...data,
            jenis_jabatan: isGuru ? 'Guru' : 'Non-Guru',
            is_baru_naik_jenjang: data.is_baru_naik_jenjang || 'Tidak', // AMBIL DATA BARU
            tanggal_lahir: formatDate(data.tanggal_lahir), tmt_golongan: formatDate(data.tmt_golongan),
            tmt_jabatan: formatDate(data.tmt_jabatan), tmt_jabatan_baru: formatDate(data.tmt_jabatan_baru),
            masa_penilaian_mulai: formatDate(data.masa_penilaian_mulai), 
            masa_penilaian_selesai: defaultSelesai
          });
        }
      }
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleChange = (e) => {
      const { name, value } = e.target;
      if (name === 'jenis_pak' && value === 'Integrasi') {
          setFormData({ ...formData, [name]: value, masa_penilaian_selesai: '2022-12-31' });
      } else {
          setFormData({ ...formData, [name]: value });
      }
  };

  const handleGolonganChange = (e) => {
    const selectedGol = e.target.value;
    const found = filteredPangkat.find(item => item.gol === selectedGol);
    setFormData({
        ...formData, golongan: selectedGol, pangkat: found ? found.pangkat : '', 
        jabatan: formData.jenis_jabatan === 'Guru' ? getAutoJabatan(selectedGol) : formData.jabatan, 
        is_jabatan_sesuai: 'Ya', jabatan_baru: '', tmt_jabatan_baru: '', is_baru_naik_jenjang: 'Tidak' // Reset saat ubah gol
    });
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    if (pakStatus.isLocked) return;
    
    let requiredFieldsMap = {
        jenis_pak: "Jenis PAK Terakhir", full_name: "Nama Lengkap", nip: "NIP", karpeg: "No. Seri Karpeg",
        tempat_lahir: "Tempat Lahir", tanggal_lahir: "Tanggal Lahir", jenis_kelamin: "Jenis Kelamin",
        pendidikan_pangkat: "Pendidikan Sesuai SK Pangkat", golongan: "Golongan", pangkat: "Pangkat",
        tmt_golongan: "TMT Golongan Terakhir", masa_kerja_tahun: "Masa Kerja (Tahun)", masa_kerja_bulan: "Masa Kerja (Bulan)",
        jabatan: "Jabatan Fungsional", tmt_jabatan: "TMT Jabatan Fungsional", unit_kerja: "Unit Kerja",
        masa_penilaian_mulai: "Mulai Penilaian (Periode PAK)", masa_penilaian_selesai: "Selesai Penilaian (Periode PAK)"
    };

    if (formData.is_pencantuman_gelar === 'Ya') {
        requiredFieldsMap = { ...requiredFieldsMap, pendidikan_gelar_baru: "Tingkat Pendidikan Baru", nama_pendidikan_baru: "Nama Jurusan / Program Studi", tahun_gelar_baru: "Tahun Lulus (Sesuai Ijazah)", tahun_usulan_gelar: "Tahun Usulan Pencantuman Gelar" };
    }

    const currentGolongan = String(formData.golongan || '').trim().toLowerCase();
    const isSpecialGol = currentGolongan === 'iii/b' || currentGolongan === 'iii/d' || currentGolongan === 'iv/c';

    if (isSpecialGol && formData.is_jabatan_sesuai === 'Tidak') {
        requiredFieldsMap = { ...requiredFieldsMap, jabatan_baru: "Jabatan Baru", tmt_jabatan_baru: "TMT Jabatan Baru" };
    }

    const missing = Object.keys(requiredFieldsMap).filter(key => {
        const value = formData[key];
        return value === undefined || value === null || String(value).trim() === '';
    });

    let errors = missing.map(key => requiredFieldsMap[key]);

    if (formData.masa_penilaian_mulai && formData.masa_penilaian_selesai) {
        const tglMulai = new Date(formData.masa_penilaian_mulai);
        const tglSelesai = new Date(formData.masa_penilaian_selesai);
        if (tglMulai > tglSelesai) {
            errors.push("Tanggal 'Mulai Penilaian' tidak boleh melebihi 'Selesai Penilaian'");
        }
    }

    if (errors.length > 0) {
        setMissingFieldsList(errors);
        setShowErrorModal(true);
    } else {
        setShowConfirmModal(true);
    }
  };

  const executeSubmit = async () => {
    setShowConfirmModal(false);
    setIsSaving(true);
    try {
      const payload = { ...formData };
      delete payload.jenis_jabatan; 

      const res = await fetch(`${BASE_URL}/api/profile`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
         showNotification("Data Profil berhasil disimpan dengan aman!", "success");
         if (onSwitchTab) onSwitchTab('uploadberkas');
      } else {
         showNotification("Gagal menyimpan data ke server.", "error");
      }
    } catch (err) { showNotification("Terjadi kesalahan jaringan/koneksi.", "error"); } 
    finally { setIsSaving(false); }
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Memuat data...</div>;

  const currentGolongan = String(formData.golongan || '').trim().toLowerCase();
  const isSpecialGol = currentGolongan === 'iii/b' || currentGolongan === 'iii/d' || currentGolongan === 'iv/c';
  const isGolKhususReset = ['iii/c', 'iv/a', 'iv/d'].includes(currentGolongan); // DETEKSI GOLONGAN BARU NAIK JENJANG
  const isIntegrasi = formData.jenis_pak === 'Integrasi';

  return (
    <div className="max-w-5xl mx-auto pb-20 px-4 md:px-8 py-6 relative"> 
      
      {toast.show && (
        <div className={`fixed top-6 right-6 z-[60] flex items-center gap-3 px-6 py-4 rounded-xl shadow-2xl text-white ${toast.type === 'success' ? 'bg-emerald-600' : 'bg-rose-600'}`}>
          {toast.type === 'success' ? <CheckCircle size={24} /> : <XCircle size={24} />}
          <p className="font-semibold">{toast.message}</p>
        </div>
      )}

      {showErrorModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4 py-8">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-full flex flex-col overflow-hidden animate-fade-in-up">
            <div className="bg-rose-50 p-6 flex flex-col items-center border-b border-rose-100 flex-shrink-0">
              <div className="bg-rose-100 p-3 rounded-full mb-3 text-rose-600"><AlertTriangle size={32} /></div>
              <h3 className="font-bold text-xl text-rose-800">Penyimpanan Gagal</h3>
              <p className="text-sm text-rose-600 text-center mt-1">Sistem mendeteksi ada data wajib yang belum Anda isi.</p>
            </div>
            <div className="p-6 overflow-y-auto custom-scrollbar">
              <p className="text-sm font-bold text-slate-700 mb-3">Mohon lengkapi isian berikut:</p>
              <ul className="space-y-2 mb-6">
                {missingFieldsList.map((field, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 p-2.5 rounded-lg border border-slate-100 font-medium">
                    <div className="w-1.5 h-1.5 rounded-full bg-rose-500 flex-shrink-0"></div> {field}
                  </li>
                ))}
              </ul>
              <button onClick={() => setShowErrorModal(false)} className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 rounded-xl transition-all sticky bottom-0">
                Saya Mengerti, Lengkapi Data
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4 py-6">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] flex flex-col overflow-hidden animate-fade-in-up">
            <div className="bg-blue-50 p-5 border-b border-blue-100 flex items-center gap-4 flex-shrink-0">
              <div className="bg-blue-100 p-3 rounded-full text-blue-600 shadow-inner"><FileText size={28} /></div>
              <div>
                <h3 className="font-bold text-xl text-blue-900">Review Seluruh Data Profil</h3>
                <p className="text-sm text-blue-700 mt-0.5">Pastikan semua data di bawah ini sudah akurat sebelum Anda menyimpannya.</p>
              </div>
            </div>

            <div className="p-6 bg-slate-50/50 overflow-y-auto custom-scrollbar flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                <div className="col-span-full text-xs font-black text-slate-400 uppercase tracking-wider border-b border-slate-200 pb-1 mt-2">Identitas Pribadi</div>
                <ReviewRow label="Nama Lengkap" value={formData.full_name} />
                <ReviewRow label="NIP" value={formData.nip} />
                <ReviewRow label="No. Karpeg" value={formData.karpeg} />
                <ReviewRow label="Tempat Lahir" value={formData.tempat_lahir} />
                <ReviewRow label="Tanggal Lahir" value={formatReviewDate(formData.tanggal_lahir)} />
                <ReviewRow label="Jenis Kelamin" value={formData.jenis_kelamin} />

                <div className="col-span-full text-xs font-black text-slate-400 uppercase tracking-wider border-b border-slate-200 pb-1 mt-4">Pendidikan</div>
                <ReviewRow label="Pendidikan Terakhir" value={formData.pendidikan_pangkat} />
                <ReviewRow label="Pencantuman Gelar Baru?" value={formData.is_pencantuman_gelar} />
                {formData.is_pencantuman_gelar === 'Ya' && (
                  <>
                    <ReviewRow label="Tingkat Gelar" value={formData.pendidikan_gelar_baru} />
                    <ReviewRow label="Nama Prodi/Jurusan" value={formData.nama_pendidikan_baru} />
                    <ReviewRow label="Tahun Lulus" value={formData.tahun_gelar_baru} />
                    <ReviewRow label="Tahun Usulan" value={formData.tahun_usulan_gelar} />
                  </>
                )}

                <div className="col-span-full text-xs font-black text-slate-400 uppercase tracking-wider border-b border-slate-200 pb-1 mt-4">Kepangkatan & Jabatan</div>
                <ReviewRow label="Golongan" value={formData.golongan} />
                <ReviewRow label="Pangkat" value={formData.pangkat} />
                <ReviewRow label="TMT Golongan" value={formatReviewDate(formData.tmt_golongan)} />
                {isGolKhususReset && (
                    <ReviewRow label="Baru Naik Jenjang?" value={formData.is_baru_naik_jenjang} />
                )}
                <ReviewRow label="Masa Kerja" value={`${formData.masa_kerja_tahun || 0} Tahun, ${formData.masa_kerja_bulan || 0} Bulan`} />
                <ReviewRow label="Jabatan Fungsional" value={formData.jabatan} />
                <ReviewRow label="TMT Jabatan Fungsional" value={formatReviewDate(formData.tmt_jabatan)} />
                <ReviewRow label="Unit Kerja" value={formData.unit_kerja} />
                
                {isSpecialGol && (
                  <div className="col-span-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4 bg-orange-50 p-3 rounded-lg border border-orange-100">
                    <ReviewRow label="Jabatan Sesuai Saat Ini?" value={formData.is_jabatan_sesuai === 'Ya' ? 'Ya, Sesuai' : 'Belum (Masih Jabatan Lama)'} />
                    {formData.is_jabatan_sesuai === 'Tidak' && (
                      <>
                        <ReviewRow label="Jabatan Baru" value={formData.jabatan_baru} />
                        <ReviewRow label="TMT Jabatan Baru" value={formatReviewDate(formData.tmt_jabatan_baru)} />
                      </>
                    )}
                  </div>
                )}

                <div className="col-span-full text-xs font-black text-slate-400 uppercase tracking-wider border-b border-slate-200 pb-1 mt-4">Data PAK Terakhir</div>
                <ReviewRow label="Jenis PAK" value={formData.jenis_pak} />
                <ReviewRow label="Mulai Penilaian" value={formatReviewDate(formData.masa_penilaian_mulai)} />
                <ReviewRow label="Selesai Penilaian" value={formatReviewDate(formData.masa_penilaian_selesai)} />
              </div>
            </div>

            <div className="p-5 bg-white border-t border-slate-200 flex justify-end gap-3 flex-shrink-0">
              <button type="button" onClick={() => setShowConfirmModal(false)} className="px-6 py-2.5 bg-slate-100 text-slate-700 hover:bg-slate-200 font-bold rounded-xl transition-all">Cek Ulang Data</button>
              <button type="button" onClick={executeSubmit} className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-lg transition-all flex items-center justify-center gap-2">
                <CheckCircle size={18}/> Simpan & Lanjut Upload
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 flex flex-col gap-2">
        <h1 className="text-2xl md:text-3xl font-serif text-slate-900">Profil Pegawai</h1>
      </div>

      {pakStatus.isLocked ? (
        <div className={`mb-6 p-4 rounded-xl border flex items-start gap-3 shadow-sm ${pakStatus.status === 'Disetujui' ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-slate-50 border-slate-200 text-slate-800'}`}>
          <Lock className={`shrink-0 ${pakStatus.status === 'Disetujui' ? 'text-blue-600' : 'text-slate-600'} mt-0.5`} size={22} />
          <div>
            <h3 className="font-bold text-sm">{pakStatus.status === 'Disetujui' ? 'Profil Pegawai (Telah Disetujui)' : 'Profil Sedang Diverifikasi'}</h3>
            <p className="text-sm mt-1">{pakStatus.status === 'Disetujui' ? 'Biodata sudah disetujui dan tidak dapat diubah lagi.' : 'Data sedang dikunci dalam mode "Read-Only" karena usulan sedang dalam proses verifikasi.'}</p>
          </div>
        </div>
      ) : (
        <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg shadow-sm flex items-start gap-3">
          <Info className="text-blue-600 shrink-0 mt-0.5" size={22} />
          <div>
            <h3 className="font-bold text-blue-900 text-sm">Petunjuk Pengisian Profil</h3>
            <p className="text-sm text-blue-800 mt-1 leading-relaxed">
              Bapak/Ibu dimohon untuk <b>mengisi seluruh kolom data di bawah ini</b>. Pastikan data terisi dengan lengkap dan sesuai dengan SK terbaru. Data yang valid sangat diperlukan agar sistem dapat menghitung Angka Kredit (PAK) Bapak/Ibu dengan tepat.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handlePreSubmit} className="space-y-6">
        
        <SectionCard title="JENIS PAK TERAKHIR YANG DIMILIKI" icon={<FileBadge className="text-blue-600"/>}>
          <div className="flex gap-6">
            <label className={`flex items-center gap-2 ${pakStatus.isLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
              <input type="radio" name="jenis_pak" value="Integrasi" checked={formData.jenis_pak === 'Integrasi'} onChange={handleChange} disabled={pakStatus.isLocked} className="w-5 h-5 text-blue-600" />
              <span className="font-bold">Integrasi</span>
            </label>
            <label className={`flex items-center gap-2 ${pakStatus.isLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
              <input type="radio" name="jenis_pak" value="Konversi" checked={formData.jenis_pak === 'Konversi'} onChange={handleChange} disabled={pakStatus.isLocked} className="w-5 h-5 text-purple-600" />
              <span className="font-bold">Konversi</span>
            </label>
          </div>
        </SectionCard>

        <SectionCard title="Identitas Pribadi" icon={<User className="text-amber-600"/>}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <InputGroup label="Nama Lengkap" name="full_name" val={formData.full_name} onChange={handleChange} readOnly={pakStatus.isLocked} />
            <InputGroup label="NIP" name="nip" val={formData.nip} onChange={handleChange} readOnly={pakStatus.isLocked} />
            <InputGroup label="No. Seri Karpeg" name="karpeg" val={formData.karpeg} onChange={handleChange} readOnly={pakStatus.isLocked} />
            <div className="grid grid-cols-2 gap-3">
              <InputGroup label="Tempat Lahir" name="tempat_lahir" val={formData.tempat_lahir} onChange={handleChange} readOnly={pakStatus.isLocked} />
              <InputGroup label="Tgl Lahir" type="date" name="tanggal_lahir" val={formData.tanggal_lahir} onChange={handleChange} readOnly={pakStatus.isLocked} />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">Jenis Kelamin</label>
              <select name="jenis_kelamin" value={formData.jenis_kelamin} onChange={handleChange} disabled={pakStatus.isLocked} className={`w-full p-2.5 md:p-3 border rounded-lg outline-none ${pakStatus.isLocked ? 'bg-slate-200 text-slate-500 cursor-not-allowed border-slate-300' : 'bg-slate-50 focus:border-amber-600'}`}>
                <option value="">-- Pilih Jenis Kelamin --</option>
                <option value="Laki-laki">Laki-laki</option>
                <option value="Perempuan">Perempuan</option>
              </select>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Pendidikan" icon={<GraduationCap className="text-amber-600"/>}>
          <div className="space-y-4">
            <InputGroup label="Pendidikan Sesuai SK Pangkat Terakhir" name="pendidikan_pangkat" val={formData.pendidikan_pangkat} onChange={handleChange} placeholder="Contoh: S1 Pendidikan Guru Sekolah Dasar" readOnly={pakStatus.isLocked} />
            
            <div className={`p-4 rounded-lg border ${pakStatus.isLocked ? 'bg-slate-100 border-slate-300' : 'bg-slate-50 border-slate-200'}`}>
              <label className="block text-sm font-bold text-slate-700 mb-2">Apakah ada pencantuman gelar baru?</label>
              <div className="flex gap-4 mb-4">
                <label className={`flex items-center gap-2 ${pakStatus.isLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}><input type="radio" name="is_pencantuman_gelar" value="Ya" checked={formData.is_pencantuman_gelar === 'Ya'} onChange={handleChange} disabled={pakStatus.isLocked} /> Ya</label>
                <label className={`flex items-center gap-2 ${pakStatus.isLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}><input type="radio" name="is_pencantuman_gelar" value="Tidak" checked={formData.is_pencantuman_gelar === 'Tidak'} onChange={handleChange} disabled={pakStatus.isLocked} /> Tidak</label>
              </div>

              {formData.is_pencantuman_gelar === 'Ya' && (
                <div className="space-y-4 animate-fade-in-up">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Tingkat Pendidikan Baru</label>
                      <select name="pendidikan_gelar_baru" value={formData.pendidikan_gelar_baru} onChange={handleChange} disabled={pakStatus.isLocked} className={`w-full p-2.5 md:p-3 border rounded-lg outline-none ${pakStatus.isLocked ? 'bg-slate-200 text-slate-500 cursor-not-allowed border-slate-300' : 'bg-white focus:border-amber-600'}`}>
                        <option value="">-- Pilih Tingkat --</option>
                        <option value="S1">S1</option><option value="S2">S2</option><option value="S3">S3</option>
                      </select>
                    </div>
                    <InputGroup label="Nama Jurusan / Program Studi" name="nama_pendidikan_baru" val={formData.nama_pendidikan_baru} onChange={handleChange} placeholder="Contoh: Manajemen Pendidikan" readOnly={pakStatus.isLocked} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputGroup label="Tahun Lulus (Sesuai Ijazah)" type="number" name="tahun_gelar_baru" val={formData.tahun_gelar_baru} onChange={handleChange} placeholder="Contoh: 2024" readOnly={pakStatus.isLocked} />
                    <InputGroup label="Tahun Usulan Pencantuman Gelar" type="number" name="tahun_usulan_gelar" val={formData.tahun_usulan_gelar} onChange={handleChange} placeholder="Contoh: 2025" readOnly={pakStatus.isLocked} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </SectionCard>

       <SectionCard title="Kepangkatan & Jabatan" icon={<FileBadge className="text-amber-600"/>}>
          
          <div className="col-span-1 md:col-span-2 mb-4 bg-amber-50 p-4 rounded-xl border border-amber-200">
             <label className="block text-sm font-bold text-amber-900 mb-3 uppercase tracking-wider">Jenis Jabatan Fungsional</label>
             <div className="flex flex-wrap gap-6">
                <label className={`flex items-center gap-2 ${pakStatus.isLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
                  <input type="radio" name="jenis_jabatan" value="Guru" checked={formData.jenis_jabatan === 'Guru'} onChange={(e) => {
                      handleChange(e);
                      setFormData(prev => ({...prev, jenis_jabatan: 'Guru', jabatan: getAutoJabatan(prev.golongan)}));
                  }} disabled={pakStatus.isLocked} className="w-5 h-5 text-amber-600" />
                  <span className="font-bold text-slate-700">GURU</span>
                </label>
                <label className={`flex items-center gap-2 ${pakStatus.isLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
                  <input type="radio" name="jenis_jabatan" value="Non-Guru" checked={formData.jenis_jabatan === 'Non-Guru'} onChange={(e) => {
                      handleChange(e);
                      setFormData(prev => ({...prev, jenis_jabatan: 'Non-Guru', jabatan: ''}));
                  }} disabled={pakStatus.isLocked} className="w-5 h-5 text-amber-600" />
                  <span className="font-bold text-slate-700">NON-GURU (Pengawas/Penilik/Pamong/dsb)</span>
                </label>
             </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            
            {/* --- BARIS 1: Golongan & Pangkat Sejajar --- */}
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">Golongan</label>
              <select name="golongan" value={formData.golongan} onChange={handleGolonganChange} disabled={pakStatus.isLocked} className={`w-full p-2.5 md:p-3 border rounded-lg outline-none ${pakStatus.isLocked ? 'bg-slate-200 text-slate-500 cursor-not-allowed border-slate-300' : 'bg-slate-50 focus:border-amber-600'}`}>
                <option value="">-- Pilih Golongan (Min. III/a) --</option>
                {filteredPangkat.map((item, idx) => <option key={idx} value={item.gol}>{item.gol}</option>)}
              </select>
            </div>

            <div>
                <InputGroup label="Pangkat (Otomatis)" val={formData.pangkat} readOnly />
            </div>

            {/* --- CHECKBOX RESET 0 AK (Memanjang Penuh) --- 
            {isGolKhususReset && (
                <div className="md:col-span-2 p-3 bg-rose-50 border border-rose-200 rounded-lg">
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input type="checkbox" name="is_baru_naik_jenjang" checked={formData.is_baru_naik_jenjang === 'Ya'} onChange={(e) => handleChange({target: {name: 'is_baru_naik_jenjang', value: e.target.checked ? 'Ya' : 'Tidak'}})} className="mt-1 w-4 h-4 text-rose-600 cursor-pointer" disabled={pakStatus.isLocked} />
                        <span className="text-sm font-bold text-rose-900 leading-tight">
                            Pegawai baru naik jenjang jabatan ke {formData.golongan}?
                            <span className="block text-xs font-normal text-rose-700 mt-0.5">Centang jika "Ya" (Sesuai aturan, Angka Kredit Sebelumnya akan di-reset menjadi 0).</span>
                        </span>
                    </label>
                </div>
            )}
                */}

            {/* --- BARIS 2: TMT Golongan & Masa Kerja Sejajar --- */}
            <div>
                <InputGroup label="TMT Golongan Terakhir" type="date" name="tmt_golongan" val={formData.tmt_golongan} onChange={handleChange} readOnly={pakStatus.isLocked} />
            </div>
            
            <div>
               <label className="block text-sm font-bold text-slate-700 mb-2">Masa Kerja (Sesuai SK Pangkat Terakhir)</label>
               <div className="flex gap-2">
                  <div className="flex-1 flex items-center gap-2">
                    <input type="number" name="masa_kerja_tahun" value={formData.masa_kerja_tahun} onChange={handleChange} readOnly={pakStatus.isLocked} className={`w-full p-2.5 border rounded-lg outline-none ${pakStatus.isLocked ? 'bg-slate-200 text-slate-500 cursor-not-allowed border-slate-300' : 'focus:border-amber-600'}`} placeholder="0" /> <span className="text-sm">Tahun</span>
                  </div>
                  <div className="flex-1 flex items-center gap-2">
                    <input type="number" name="masa_kerja_bulan" value={formData.masa_kerja_bulan} onChange={handleChange} readOnly={pakStatus.isLocked} className={`w-full p-2.5 border rounded-lg outline-none ${pakStatus.isLocked ? 'bg-slate-200 text-slate-500 cursor-not-allowed border-slate-300' : 'focus:border-amber-600'}`} placeholder="0" /> <span className="text-sm">Bulan</span>
                  </div>
               </div>
            </div>
            
            {/* --- BARIS 3: Jabatan Fungsional --- */}
            <div className="md:col-span-2 pt-4 border-t mt-2">
               {formData.jenis_jabatan === 'Guru' ? (
                   <InputGroup label="JABATAN FUNGSIONAL (Otomatis)" val={formData.jabatan} readOnly />
               ) : (
                   <InputGroup 
                     label="Sebutkan Jabatan Fungsional Faktual (Contoh: Pengawas Ahli Madya)" 
                     name="jabatan" 
                     val={formData.jabatan} 
                     onChange={handleChange} 
                     readOnly={pakStatus.isLocked} 
                   />
               )}

               <div className="mt-4">
                  <InputGroup label="TMT Jabatan Fungsional" type="date" name="tmt_jabatan" val={formData.tmt_jabatan} onChange={handleChange} readOnly={pakStatus.isLocked} />
               </div>

               {isSpecialGol && (
                  <div className="mt-4 p-5 bg-orange-50 border border-orange-200 rounded-xl animate-fade-in-up">
                     <label className="block text-sm font-bold text-orange-900 mb-3">APAKAH JABATAN FUNGSIONAL DI ATAS SUDAH SESUAI JABATAN FUNGSIONAL PER SAAT INI?</label>
                     <div className="flex gap-4 mb-4">
                        <label className={`flex items-center gap-2 ${pakStatus.isLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}><input type="radio" name="is_jabatan_sesuai" value="Ya" checked={formData.is_jabatan_sesuai === 'Ya'} onChange={handleChange} disabled={pakStatus.isLocked} className="w-4 h-4 text-orange-600" /> Ya, Sudah Sesuai</label>
                        <label className={`flex items-center gap-2 ${pakStatus.isLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}><input type="radio" name="is_jabatan_sesuai" value="Tidak" checked={formData.is_jabatan_sesuai === 'Tidak'} onChange={handleChange} disabled={pakStatus.isLocked} className="w-4 h-4 text-orange-600" /> Belum (Masih Jabatan Lama)</label>
                     </div>
                     {formData.is_jabatan_sesuai === 'Tidak' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-orange-200">
                           <div>
                              {formData.jenis_jabatan === 'Guru' ? (
                                  <>
                                    <label className="block text-sm font-bold mb-2 text-slate-700">Jabatan Baru</label>
                                    <select name="jabatan_baru" value={formData.jabatan_baru} onChange={handleChange} disabled={pakStatus.isLocked} className={`w-full p-2.5 border rounded-lg outline-none ${pakStatus.isLocked ? 'bg-slate-200 text-slate-500 cursor-not-allowed border-slate-300' : 'bg-white focus:border-amber-600'}`}>
                                      <option value="">-- Pilih Jabatan Baru --</option>
                                      <option value="Guru Ahli Pertama">Guru Ahli Pertama</option>
                                      <option value="Guru Ahli Muda">Guru Ahli Muda</option>
                                      <option value="Guru Ahli Madya">Guru Ahli Madya</option>
                                      <option value="Guru Ahli Utama">Guru Ahli Utama</option>
                                    </select>
                                  </>
                              ) : (
                                  <InputGroup label="Sebutkan Jabatan Baru" name="jabatan_baru" val={formData.jabatan_baru} onChange={handleChange} readOnly={pakStatus.isLocked} />
                              )}
                           </div>
                           <InputGroup label="TMT Jabatan Baru" type="date" name="tmt_jabatan_baru" val={formData.tmt_jabatan_baru} onChange={handleChange} readOnly={pakStatus.isLocked} />
                        </div>
                     )}
                  </div>
               )}
            </div>

            <div className="md:col-span-2">
               <InputGroup label="Unit Kerja" name="unit_kerja" val={formData.unit_kerja} onChange={handleChange} readOnly={pakStatus.isLocked} />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="PERIODE PENILAIAN SESUAI PAK TERAKHIR" icon={<CalendarClock className="text-amber-600"/>}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
             <InputGroup 
                 label="Mulai Penilaian" 
                 type="date" 
                 name="masa_penilaian_mulai" 
                 val={formData.masa_penilaian_mulai} 
                 onChange={handleChange} 
                 readOnly={pakStatus.isLocked}
                 maxDate={formData.masa_penilaian_selesai ? new Date(formData.masa_penilaian_selesai) : null}
             />
             <InputGroup 
                 label="Selesai Penilaian" 
                 type="date" 
                 name="masa_penilaian_selesai" 
                 val={formData.masa_penilaian_selesai} 
                 onChange={handleChange} 
                 readOnly={pakStatus.isLocked || isIntegrasi} 
             />
          </div>
          
          <div className="mt-5 p-4 bg-slate-100 border-l-4 border-amber-500 rounded-r-lg">
            <div className="flex items-start gap-3">
              <Info className="text-amber-600 shrink-0 mt-0.5" size={20}/>
              <div className="text-sm text-slate-700 leading-relaxed">
                <strong className="text-slate-800 block mb-1">Informasi Masa Penilaian Bapak/Ibu:</strong>
                {isIntegrasi ? (
                  <span>Karena Bapak/Ibu menggunakan <b>PAK Integrasi</b>, maka batas akhir penilaian pada sistem secara otomatis dikunci pada tanggal <b>31 Desember 2022</b>.</span>
                ) : formData.is_jabatan_sesuai === 'Ya' ? (
                  <span>Berdasarkan profil Bapak/Ibu dengan Jabatan Fungsional yang <b>masih sesuai</b>, maka masa penilaian selanjutnya akan dihitung hingga batas akhir tanggal <b>31 Desember 2025</b>.</span>
                ) : (
                  <span>Karena terdapat <b>Penyesuaian Jabatan Baru</b>, maka masa penilaian selanjutnya akan dihitung dan berakhir pada <b>akhir bulan sebelum TMT Jabatan Baru</b> Bapak/Ibu.</span>
                )}
              </div>
            </div>
          </div>
        </SectionCard>

        {!pakStatus.isLocked && (
          <div className="flex justify-end pt-4 border-t border-slate-200 mt-6">
            <button type="submit" disabled={isSaving} className={`w-full py-4 text-base md:text-lg rounded-xl font-bold flex gap-2 items-center justify-center shadow-lg transition-all ${isSaving ? 'bg-slate-400 cursor-not-allowed text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}>
              {isSaving ? <Loader2 className="animate-spin"/> : <Save size={24}/>} {isSaving ? 'MENYIMPAN DATA...' : 'SIMPAN DAN LANJUT UPLOAD'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

const SectionCard = ({ title, icon, children }) => (
  <div className="bg-white p-4 md:p-8 rounded-2xl border border-slate-200 shadow-sm">
    <div className="flex items-center gap-3 mb-4 md:mb-6 pb-3 md:pb-4 border-b border-slate-100">
      <div className="p-2 bg-amber-50 rounded-lg">{icon}</div><h3 className="text-lg font-bold uppercase tracking-wide text-slate-800">{title}</h3>
    </div>{children}
  </div>
);

const ReviewRow = ({ label, value }) => (
  <div className="flex flex-col border-b border-slate-100 pb-2">
    <span className="text-[10px] font-bold text-slate-500 uppercase">{label}</span>
    <span className="text-sm font-bold text-slate-800">{value || '-'}</span>
  </div>
);

const InputGroup = ({ label, name, val, onChange, type = "text", readOnly = false, placeholder = "", maxDate }) => {
  if (type === 'date') {
    const parseDate = (str) => {
      if (!str) return null;
      const parts = str.split('-');
      if (parts.length === 3) return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
      return null;
    };

    return (
      <div className="flex flex-col">
        <label className="block text-sm font-bold text-slate-700 mb-1.5">{label}</label>
        <DatePicker
          selected={parseDate(val)}
          onChange={(date) => {
            if (!date) {
               if (onChange) onChange({ target: { name, value: '' } });
               return;
            }
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            if (onChange) onChange({ target: { name, value: `${y}-${m}-${d}` } });
          }}
          onChangeRaw={(e) => e.preventDefault()} 
          dateFormat="dd/MM/yyyy"
          placeholderText="Pilih dari Kalender 📅"
          readOnly={readOnly}
          disabled={readOnly}
          maxDate={maxDate}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          wrapperClassName="w-full"
          className={`w-full p-2.5 border rounded-lg font-medium text-sm transition-colors ${readOnly ? 'bg-slate-200 text-slate-500 cursor-not-allowed border-slate-300' : 'bg-slate-50 focus:border-amber-600 focus:bg-white outline-none cursor-pointer'}`}
        />
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-bold text-slate-700 mb-1.5">{label}</label>
      <input 
        type={type} 
        name={name} 
        value={val || ''} 
        onChange={!readOnly ? onChange : undefined} 
        readOnly={readOnly} 
        placeholder={placeholder} 
        className={`w-full p-2.5 border rounded-lg font-medium text-sm transition-colors ${readOnly ? 'bg-slate-200 text-slate-500 cursor-not-allowed border-slate-300' : 'bg-slate-50 focus:border-amber-600 focus:bg-white outline-none'}`} 
      />
    </div>
  );
};

export default ProfilePage;